import { useEffect } from 'react';
import loadScript from '../utils/loadScript';

/**
 * Utility to execute reCAPTCHA Enterprise and get a token for a given action.
 *
 * @param siteKey reCAPTCHA site key
 * @param action Action name (should match one of RECAPTCHA_EVENTS)
 * @returns Promise<string> reCAPTCHA token
 */
export const executeRecaptcha = (siteKey: string, action: string): Promise<string | undefined> => {
  return new Promise((resolve) => {
    if (!window.grecaptcha || !window.grecaptcha.enterprise) {
      console.warn('Enterprise reCAPTCHA not loaded');
      return resolve(undefined);
    }
    window.grecaptcha.enterprise.ready(async () => {
      try {
        const token = await window.grecaptcha.enterprise.execute(siteKey, { action });
        resolve(token);
      } catch (err) {
        console.warn('Enterprise reCAPTCHA tracking failed', err);
        resolve(undefined);
      }
    });
  });
};

/**
 * Injects the reCaptcha Enterprise script using loadScript util.
 * @param siteKey The reCaptcha site key to use in the script src.
 * @param onLoad Optional callback when script is loaded.
 * @param onError Optional callback when script fails to load.
 */
export const useEnterpriseRecaptcha = (siteKey?: string, onLoad?: () => void, onError?: () => void) => {
  useEffect(() => {
    if (!siteKey) return;
    const scriptId = 'recaptcha-enterprise-script';
    const url = `https://www.google.com/recaptcha/enterprise.js?render=${siteKey}`;
    loadScript({ url, id: scriptId })
      .then(() => {
        onLoad?.();
      })
      .catch(() => {
        onError?.();
      });
    // No cleanup: Google recommends script stays for re-use
  }, [siteKey, onLoad, onError]);
};
